import React, { useState, useEffect, useCallback, createContext, useContext, Suspense } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { initializeApp, getApps } from 'firebase/app';
import api from './api';
import ErrorBoundary from './components/ErrorBoundary';
import { canAccessPage } from './utils/teamAccess';
import TeamWorkspaceHome from './components/TeamWorkspaceHome';
import SubscriptionExpiry from './components/SubscriptionExpiry';
import WorkspaceNotifications from './components/WorkspaceNotifications';
import PaymentCelebration from './components/PaymentCelebration';
import './styles/subscription-expiry.css';

const Header = React.lazy(() => import('./components/Header'));
const Sidebar = React.lazy(() => import('./components/Sidebar'));
const SignIn = React.lazy(() => import('./components/LogIns/SignIn'));
const Landing = React.lazy(() => import('./pages/Landing'));
const PaymentStatus = React.lazy(() => import('./components/LogIns/PaymentStatus'));
const Admin = React.lazy(() => import('./pages/Admin'));
const AccountAccess = React.lazy(() => import('./components/AccountAccess'));
const IDCards = React.lazy(() => import('./pages/IDCards'));
const Seats = React.lazy(() => import('./pages/Seats'));
const Floor = React.lazy(() => import('./components/Floor'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Students = React.lazy(() => import('./pages/Students'));
const Enquiries = React.lazy(() => import('./pages/Bookings'));
const FeePlans = React.lazy(() => import('./pages/FeePlans'));
const Notifications = React.lazy(() => import('./pages/Notifications'));
const Reminders = React.lazy(() => import('./pages/Reminders'));
const Reports = React.lazy(() => import('./pages/Reports'));
const Expense = React.lazy(() => import('./pages/Expense'));
const Mess = React.lazy(() => import('./pages/Mess'));
const Branches = React.lazy(() => import('./pages/Branches'));
const Staff = React.lazy(() => import('./pages/Staff'));
const CheckInOut = React.lazy(() => import('./pages/CheckInOut'));
const ProfitLoss = React.lazy(() => import('./pages/ProfitLoss'));
const Operations = React.lazy(() => import('./pages/Operations'));
const TeamAccess = React.lazy(() => import('./pages/TeamAccess'));

// Public Pages - No Authentication Required
const ContactUs = React.lazy(() => import('./pages/ContectUs'));
const PrivacyPolicy = React.lazy(() => import('./pages/Privacypolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfServices'));
const DeleteAccount = React.lazy(() => import('./pages/DeleteAccount'));

const firebaseConfig = {
   apiKey: "AIzaSyBhyV-7A75rkHQ1ouDBtMWWn1PWFsIX1Fg",
  authDomain: "libmate-signin.firebaseapp.com",
  projectId: "libmate-signin",
  storageBucket: "libmate-signin.firebasestorage.app",
  messagingSenderId: "764163771306",
  appId: "1:764163771306:web:9eaf0ef8c6fa992643b9d3",
  measurementId: "G-SFQQQVXL4X"
};

if (!getApps().length) {
    const app = initializeApp(firebaseConfig);
    const auth = getAuth(app);
    setPersistence(auth, browserLocalPersistence);
}

export const AuthContext = createContext(null);

const LoadingSpinner = () => (
    <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="flex flex-col items-center gap-4">
            <div className="relative h-14 w-14">
                <div className="absolute inset-0 rounded-2xl bg-teal-400/20 animate-ping"></div>
                <div className="relative h-14 w-14 rounded-2xl border border-white/10 bg-white/10 flex items-center justify-center shadow-2xl">
                    <div className="animate-spin rounded-full h-7 w-7 border-2 border-white/20 border-t-teal-300"></div>
                </div>
            </div>
            <p className="text-sm font-medium tracking-wide text-slate-300">Preparing your workspace</p>
        </div>
    </div>
);

const AppContent = () => {
    const [user, setUser] = useState(null);
    const [authError, setAuthError] = useState('');
    const [loading, setLoading] = useState(true);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [subscriptionAccess, setSubscriptionAccess] = useState(null);
    const [mobileSidebar, setMobileSidebar] = useState(false);
    const [miniSidebar, setMiniSidebar] = useState(false);
    const [expandedItems, setExpandedItems] = useState({});
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = useCallback(async () => {
        setSubscriptionAccess(null);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('selectedBranchId');
        localStorage.removeItem('hosteldeskPasswordUser');
        try {
            const auth = getAuth();
            await signOut(auth);
            localStorage.removeItem('accessToken');
            localStorage.removeItem('hosteldeskPasswordUser');
            setUser(null);
            setPaymentStatus(null);
            navigate('/signin');
        } catch (error) {
            console.error('Logout error:', error);
            localStorage.removeItem('accessToken');
            localStorage.removeItem('hosteldeskPasswordUser');
            setUser(null);
            setPaymentStatus(null);
            navigate('/signin');
        }
    }, [navigate]);

    const signedInAccountId = user?._id;
    const signedInRole = user?.role;
    useEffect(() => {
        if (!signedInAccountId || signedInRole === 'owner') return;
        let disposed = false;
        const refresh = async () => {
            try {
                const { data } = await api.get('/user/profile');
                if (!disposed) setUser(previous => previous && previous._id === data.user._id ? { ...previous, ...data.user } : previous);
            } catch (error) { if (!disposed && [401, 403].includes(error.response?.status)) handleLogout(); }
        };
        refresh(); window.addEventListener('focus', refresh);
        const interval = setInterval(refresh, 60000);
        return () => { disposed = true; clearInterval(interval); window.removeEventListener('focus', refresh); };
    }, [signedInAccountId, signedInRole, location.pathname, handleLogout]);

    const checkPaymentStatus = useCallback(async () => {
        const session = localStorage.getItem('accessToken');
        if (!session) return;
        try {
            const response = await api.get('/user/payment-status');
            if (session !== localStorage.getItem('accessToken')) return;
            const hasPaid = response.data.PaymentStatus;
            setPaymentStatus(hasPaid);
            setSubscriptionAccess(response.data.subscriptionAccess || null);

        } catch (error) {
            console.error('Payment status check failed:', error);
            
            if (error.response) {
                if (error.response.status === 401 || error.response.status === 403) {
                    const errorMessage = error.response.data?.error?.toLowerCase() || '';
                    if (errorMessage.includes('token') || 
                        errorMessage.includes('unauthorized') || 
                        errorMessage.includes('forbidden') ||
                        errorMessage.includes('invalid') ||
                        errorMessage.includes('expired')) {
                        console.error('Authentication token invalid, logging out');
                        handleLogout();
                    }
                }
            }
        }
    }, [handleLogout]);

    useEffect(() => {
        const onInactive = event => {
            if (!localStorage.getItem('accessToken')) return;
            setSubscriptionAccess(event.detail); setPaymentStatus(false);
        };
        window.addEventListener('hosteldesk-subscription-inactive', onInactive);
        window.addEventListener('focus', checkPaymentStatus);
        return () => { window.removeEventListener('hosteldesk-subscription-inactive', onInactive); window.removeEventListener('focus', checkPaymentStatus); };
    }, [checkPaymentStatus]);
    useEffect(() => {
        if (!subscriptionAccess?.isActive || !subscriptionAccess.expiresAt) return;
        const delay = Math.max(0, new Date(subscriptionAccess.expiresAt).getTime() - Date.now() + 100);
        const timeout = setTimeout(checkPaymentStatus, Math.min(delay, 2147483647));
        return () => clearTimeout(timeout);
    }, [subscriptionAccess, checkPaymentStatus]);


    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                try {
                    setAuthError('');
                    // Get Firebase ID token for server-side verification
                    const idToken = await currentUser.getIdToken();
                    const response = await api.post('/users/auth', {
                        idToken,
                        email: currentUser.email,
                        displayName: currentUser.displayName
                    });

                    const { accessToken } = response.data;
                    if (response.data.user?.isReviewAccount) throw new Error('This sample account is disabled.');
                    localStorage.setItem('accessToken', accessToken);

                    setUser(response.data.user);
                    await checkPaymentStatus();
                } catch (error) {
                    console.error('Backend authentication error:', error);
                    setAuthError(error.response?.data?.error || 'Unable to open your workspace. Please try again.');
                    handleLogout();
                }
            } else {
                const storedToken = localStorage.getItem('accessToken');
                const storedPasswordUser = localStorage.getItem('hosteldeskPasswordUser');
                if (storedToken && storedPasswordUser) {
                    try {
                        const profile = await api.get('/user/profile');
                        if (profile.data.user?.isReviewAccount) throw new Error('This sample account is disabled.');
                        setUser({ ...profile.data.user, authProvider: 'password' });
                        await checkPaymentStatus();
                    } catch (error) {
                        localStorage.removeItem('accessToken');
                        localStorage.removeItem('hosteldeskPasswordUser');
                        localStorage.removeItem('selectedBranchId');
                        setUser(null);
                        setPaymentStatus(null);
                        setSubscriptionAccess(null);
                    }
                } else {
                    localStorage.removeItem('accessToken');
                    setUser(null);
                    setPaymentStatus(null);
                }
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [checkPaymentStatus, handleLogout]);

    useEffect(() => {
        if (user) {
            const intervalId = setInterval(() => {
                checkPaymentStatus();
            }, 300000);
            return () => clearInterval(intervalId);
        }
    }, [user, checkPaymentStatus]);
    
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (mobileSidebar && !event.target.closest('.mobile-sidebar') && !event.target.closest('.mobile-menu-button')) {
          setMobileSidebar(false);
        }
      };
      const handleResize = () => {
        if (window.innerWidth >= 768) { setMobileSidebar(false); }
      };
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', handleResize);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('resize', handleResize);
      };
    }, [mobileSidebar]);

    if (loading) {
        return <LoadingSpinner />;
    }

    const authContextValue = {
        user,
        updateUser: setUser,
        authError,
        loading,
        paymentStatus,
        subscriptionAccess,
        checkPaymentStatus,
        logout: handleLogout
    };

    const layoutProps = {
        mobileSidebar,
        setMobileSidebar,
        miniSidebar,
        setMiniSidebar,
        expandedItems,
        setExpandedItems,
    };

    return (
        <AuthContext.Provider value={authContextValue}>
            <Routes>
                {/* Public Routes - No Authentication Required */}
                <Route path="/contact-us" element={
                    <Suspense fallback={<LoadingSpinner />}><ContactUs /></Suspense>
                } />
                <Route path="/privacy-policy" element={
                    <Suspense fallback={<LoadingSpinner />}><PrivacyPolicy /></Suspense>
                } />
                <Route path="/terms-of-service" element={
                    <Suspense fallback={<LoadingSpinner />}><TermsOfService /></Suspense>
                } />
                <Route path="/delete-account" element={
                    <Suspense fallback={<LoadingSpinner />}><DeleteAccount /></Suspense>
                } />

                {/* Auth Routes */}
                <Route path="/onboarding" element={<ProtectedRoute><AccountAccess onboarding /></ProtectedRoute>} />
                <Route path="/signin" element={
                    user ? <Navigate to="/dashboard" replace /> : <Suspense fallback={<LoadingSpinner />}><SignIn /></Suspense>
                } />
                <Route path="/payment-status" element={
                    <ProtectedRoute>
                        <Suspense fallback={<LoadingSpinner />}><PaymentStatus /></Suspense>
                    </ProtectedRoute>
                } />

                {/* Protected Routes */}
                <Route path="/*" element={
                    <ProtectedRoute>
                        <Layout {...layoutProps}>
                            <Routes>
                                <Route path="dashboard" element={<Suspense fallback={<LoadingSpinner />}>{user?.permissionMode === 'features' && user.role !== 'owner' ? <TeamWorkspaceHome /> : <Dashboard />}</Suspense>} />
                                <Route path="branches" element={<Suspense fallback={<LoadingSpinner />}><Branches /></Suspense>} />
                                <Route path="admin" element={<Suspense fallback={<LoadingSpinner />}><Admin /></Suspense>} />
                                <Route path="account" element={user?.role && user.role !== 'owner' ? <Navigate to="/dashboard" replace /> : <Suspense fallback={<LoadingSpinner />}><AccountAccess /></Suspense>} />
                                <Route path="floors" element={<Suspense fallback={<LoadingSpinner />}><Floor /></Suspense>} />
                                <Route path="id-cards" element={<Suspense fallback={<LoadingSpinner />}><IDCards /></Suspense>} />
                                <Route path="seats" element={<Suspense fallback={<LoadingSpinner />}><Seats /></Suspense>} />
                                <Route path="students" element={<Suspense fallback={<LoadingSpinner />}><Students /></Suspense>} />
                                <Route path="residents" element={<Suspense fallback={<LoadingSpinner />}><Students /></Suspense>} />
                                <Route path="enquiries" element={<Suspense fallback={<LoadingSpinner />}><Enquiries /></Suspense>} />
                                <Route path="bookings" element={<Suspense fallback={<LoadingSpinner />}><Enquiries /></Suspense>} />
                                <Route path="fee-plans" element={<Suspense fallback={<LoadingSpinner />}><FeePlans /></Suspense>} />
                                <Route path="notifications" element={<Suspense fallback={<LoadingSpinner />}><Notifications /></Suspense>} />
                                <Route path="reminders" element={<Suspense fallback={<LoadingSpinner />}><Reminders /></Suspense>} />
                                <Route path="reports" element={<Suspense fallback={<LoadingSpinner />}><Reports /></Suspense>} />
                                <Route path="expenses" element={<Suspense fallback={<LoadingSpinner />}><Expense /></Suspense>} />
                                <Route path="mess" element={<Suspense fallback={<LoadingSpinner />}><Mess /></Suspense>} />
                                <Route path="staff" element={<Suspense fallback={<LoadingSpinner />}><Staff /></Suspense>} />
                                <Route path="check-in-out" element={<Suspense fallback={<LoadingSpinner />}><CheckInOut /></Suspense>} />
                                <Route path="profit-loss" element={<Suspense fallback={<LoadingSpinner />}><ProfitLoss /></Suspense>} />
                                <Route path="operations" element={<Suspense fallback={<LoadingSpinner />}><Operations /></Suspense>} />
                                <Route path="team-access" element={<Suspense fallback={<LoadingSpinner />}><TeamAccess /></Suspense>} />
                                <Route index element={<Navigate to="dashboard" replace />} />
                                <Route path="*" element={<Navigate to="dashboard" replace />} />
                            </Routes>
                        </Layout>
                    </ProtectedRoute>
                } />
            </Routes>
        </AuthContext.Provider>
    );
};

const ProtectedRoute = ({ children }) => {
    const { user, loading, paymentStatus, subscriptionAccess } = useContext(AuthContext);
    const location = useLocation();
    if (loading) { return <LoadingSpinner />; }
    if (!user) { return <Navigate to="/signin" state={{ from: location }} replace />; }
    if (!canAccessPage(user, location.pathname)) return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6"><section className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md"><h1 className="font-bold text-xl">Access not granted</h1><p className="text-slate-500 mt-3">Ask your hostel owner to grant this feature in Team Access.</p><button className="hd-primary-button mt-5" onClick={() => window.location.hash = '#/dashboard'}>Back to workspace</button></section></div>;
    if (user.role === 'owner' && user.profileCompletedAt === null && location.pathname !== '/onboarding') { return <Navigate to="/onboarding" replace />; }
    if (location.pathname === '/onboarding') { return user.profileCompletedAt === null ? <Suspense fallback={<LoadingSpinner />}>{children}</Suspense> : <Navigate to="/dashboard" replace />; }
    if (paymentStatus === false && !subscriptionAccess?.requiresRenewal && user.role !== 'owner') return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6"><section className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md"><h1 className="font-bold text-xl">Workspace access paused</h1><p className="text-slate-500 mt-3">Your hostel subscription is inactive. Contact your hostel owner to activate it. Your team permissions and data are unchanged.</p><TeamSignOut /></section></div>;
    if (paymentStatus === false && !subscriptionAccess?.requiresRenewal && location.pathname !== '/payment-status') { return <Navigate to="/payment-status" replace />; }
    return <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>;
};

const TeamSignOut = () => {
    const { logout } = useContext(AuthContext);
    return <button className="hd-secondary-button mt-5" onClick={logout}>Sign out</button>;
};

const Layout = ({ children, ...props }) => (
    <div className="app-shell hd-workspace-theme min-h-screen text-slate-900">
        <Suspense fallback={<LoadingSpinner />}>
            <Header {...props} />
            <Sidebar {...props} />
            {props.mobileSidebar && (
                <div
                    className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-40 md:hidden"
                    onClick={() => props.setMobileSidebar(false)}
                />
            )}
            <main className={`app-workspace transition-all duration-300 ease-out pt-20 ${props.miniSidebar ? 'md:ml-20' : 'md:ml-72 hd-main-expanded'} min-h-screen`}>
                <div className="app-content max-w-[1600px] mx-auto px-4 py-5 sm:px-6 sm:py-7 lg:px-8 lg:py-8 page-enter"><SubscriptionExpiry />{children}</div>
            </main>
        </Suspense>
    </div>
);

const App = () => (
  <ErrorBoundary>
    <Router>
        <WorkspaceNotifications />
        <PaymentCelebration />
        <Routes>
            <Route path="/" element={<Suspense fallback={<LoadingSpinner />}><Landing /></Suspense>} />
            <Route path="/home" element={<Suspense fallback={<LoadingSpinner />}><Landing /></Suspense>} />
            <Route path="*" element={<AppContent />} />
        </Routes>
    </Router>
  </ErrorBoundary>
);

export default App;
